import { useState, useEffect, useCallback } from "react";
import { createFileRoute } from "@tanstack/react-router";
import {
  Shield,
  Key,
  AlertTriangle,
  CheckCircle,
  Activity,
  Globe,
  Clock,
  Loader2,
  RefreshCw,
  ArrowUpDown,
} from "lucide-react";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Pagination } from "@/components/ui/pagination";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import {
  platformApi,
  AuditLogEntry,
  SecuritySettings,
  SecurityStats,
} from "@/services/api/platform";
import { usePaginatedData, PaginatedResponse } from "@/hooks/usePaginatedData";
import { useSortableData } from "@/hooks/useSortableData";

export const Route = createFileRoute("/portal/platform/security")({
  component: SecurityPage,
});

function SecurityPage() {
  const { token } = useAuth();
  const [activeTab, setActiveTab] = useState("overview");
  const [settingsLoading, setSettingsLoading] = useState(true);
  const [settings, setSettings] = useState<SecuritySettings | null>(null);
  const [stats, setStats] = useState<SecurityStats | null>(null);
  const [statsLoading, setStatsLoading] = useState(true);
  const [eventFilter, setEventFilter] = useState("all");

  // Load security settings and stats
  useEffect(() => {
    if (token) {
      loadSettings();
      loadStats();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const loadSettings = async () => {
    if (!token) return;
    setSettingsLoading(true);
    try {
      const settingsRes = await platformApi.getSecuritySettings(token);
      setSettings(settingsRes);
    } catch (error) {
      console.error("Failed to load security settings:", error);
      toast.error("Failed to load security settings");
      setSettings({
        require_mfa: false,
        allow_password_login: true,
        allow_sso_login: true,
        session_timeout_minutes: 60,
        password_min_length: 8,
        ip_whitelist_enabled: false,
        whitelisted_ips: [],
      });
    } finally {
      setSettingsLoading(false);
    }
  };

  const loadStats = async () => {
    if (!token) return;
    setStatsLoading(true);
    try {
      const statsRes = await platformApi.getSecurityStats(token);
      setStats(statsRes);
    } catch (error) {
      console.error("Failed to load security stats:", error);
      // Don't show error toast - stats are supplementary
      setStats({
        active_sessions: 0,
        failed_logins_24h: 0,
        mfa_enabled_users: 0,
        total_users: 0,
        mfa_percentage: 0,
        security_score: "Poor",
      });
    } finally {
      setStatsLoading(false);
    }
  };

  // Server-side pagination for audit logs
  const auditFetchFn = useCallback(
    async (
      page: number,
      pageSize: number,
    ): Promise<PaginatedResponse<AuditLogEntry>> => {
      if (!token) {
        return { data: [], totalCount: 0, page, pageSize };
      }
      try {
        const response = await platformApi.getAuditLogs(token, {
          skip: (page - 1) * pageSize,
          limit: pageSize,
          event_type: eventFilter !== "all" ? eventFilter : undefined,
        });
        return {
          data: response.items || [],
          totalCount: response.total,
          page,
          pageSize,
        };
      } catch (error) {
        console.error("Failed to load audit logs:", error);
        toast.error("Failed to load audit logs");
        return { data: [], totalCount: 0, page, pageSize };
      }
    },
    [token, eventFilter],
  );

  const {
    data: auditLogs,
    isLoading: auditLoading,
    pagination: auditPagination,
    goToPage: auditGoToPage,
    nextPage: auditNextPage,
    prevPage: auditPrevPage,
    firstPage: auditFirstPage,
    lastPage: auditLastPage,
    setPageSize: auditSetPageSize,
    refresh: refreshAuditLogs,
    canGoNext: auditCanGoNext,
    canGoPrev: auditCanGoPrev,
    startIndex: auditStartIndex,
    endIndex: auditEndIndex,
  } = usePaginatedData<AuditLogEntry>({
    fetchFn: auditFetchFn,
    initialPageSize: 25,
    dependencies: [token, eventFilter],
  });

  // Client-side sorting for audit logs (within current page)
  const {
    sortedData: sortedAuditLogs,
    requestSort: auditRequestSort,
    getSortDirection: auditGetSortDirection,
  } = useSortableData(auditLogs as unknown as Record<string, unknown>[], {
    initialSort: { key: "timestamp", direction: "desc" },
    columns: [
      { key: "timestamp" },
      { key: "user_email" },
      { key: "action" },
      { key: "resource_type" },
      { key: "ip_address" },
    ],
  });
  const displayAuditLogs = sortedAuditLogs as unknown as AuditLogEntry[];

  const getAuditSortIcon = (key: string) => {
    const direction = auditGetSortDirection(key);
    return (
      <ArrowUpDown
        className={`ml-1 h-3 w-3 inline ${direction ? "text-primary" : "text-muted-foreground"}`}
      />
    );
  };

  const refreshAll = () => {
    loadSettings();
    loadStats();
    refreshAuditLogs();
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString("en-US", {
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const isLoading = settingsLoading && auditLogs.length === 0;

  if (isLoading) {
    return (
      <div className="container mx-auto py-6">
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto py-6 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Shield className="h-6 w-6" />
            Security
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Platform security settings and audit logs
          </p>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={refreshAll}
          disabled={settingsLoading || statsLoading || auditLoading}
        >
          <RefreshCw
            className={`mr-2 h-4 w-4 ${settingsLoading || statsLoading || auditLoading ? "animate-spin" : ""}`}
          />
          Refresh
        </Button>
      </div>

      {/* Security Status Cards */}
      <div className="grid gap-4 md:grid-cols-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">
              Security Score
            </CardTitle>
            <Shield
              className={`h-4 w-4 ${
                stats?.security_score === "Good"
                  ? "text-green-500"
                  : stats?.security_score === "Fair"
                    ? "text-yellow-500"
                    : "text-red-500"
              }`}
            />
          </CardHeader>
          <CardContent>
            <div
              className={`text-2xl font-bold ${
                stats?.security_score === "Good"
                  ? "text-green-600"
                  : stats?.security_score === "Fair"
                    ? "text-yellow-600"
                    : "text-red-600"
              }`}
            >
              {statsLoading ? "..." : stats?.security_score ?? "—"}
            </div>
            <p className="text-xs text-muted-foreground">
              Based on MFA adoption
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">
              Active Sessions
            </CardTitle>
            <Activity className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {statsLoading ? "..." : stats?.active_sessions ?? 0}
            </div>
            <p className="text-xs text-muted-foreground">Current sessions</p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Failed Logins</CardTitle>
            <AlertTriangle
              className={`h-4 w-4 ${
                (stats?.failed_logins_24h ?? 0) > 10
                  ? "text-red-500"
                  : (stats?.failed_logins_24h ?? 0) > 0
                    ? "text-yellow-500"
                    : "text-muted-foreground"
              }`}
            />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {statsLoading ? "..." : stats?.failed_logins_24h ?? 0}
            </div>
            <p className="text-xs text-muted-foreground">Last 24 hours</p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">MFA Enabled</CardTitle>
            <Key className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {statsLoading ? "..." : `${stats?.mfa_percentage ?? 0}%`}
            </div>
            <p className="text-xs text-muted-foreground">
              {stats ? `${stats.mfa_enabled_users} of ${stats.total_users} users` : "Of all users"}
            </p>
          </CardContent>
        </Card>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="authentication">Authentication</TabsTrigger>
          <TabsTrigger value="ip-restrictions">IP Restrictions</TabsTrigger>
          <TabsTrigger value="audit-log">Audit Log</TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="mt-6 space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Security Checks</CardTitle>
              <CardDescription className="text-xs">
                Current security status of the platform
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between py-2 border-b">
                <div className="flex items-center gap-2">
                  <CheckCircle className="h-4 w-4 text-green-500" />
                  <span>HTTPS Enabled</span>
                </div>
                <Badge variant="success">Secure</Badge>
              </div>
              <div className="flex items-center justify-between py-2 border-b">
                <div className="flex items-center gap-2">
                  <CheckCircle className="h-4 w-4 text-green-500" />
                  <span>Database Encryption</span>
                </div>
                <Badge variant="success">Enabled</Badge>
              </div>
              <div className="flex items-center justify-between py-2 border-b">
                <div className="flex items-center gap-2">
                  <AlertTriangle className="h-4 w-4 text-yellow-500" />
                  <span>MFA Enforcement</span>
                </div>
                <Badge variant="secondary">Optional</Badge>
              </div>
              <div className="flex items-center justify-between py-2">
                <div className="flex items-center gap-2">
                  <CheckCircle className="h-4 w-4 text-green-500" />
                  <span>API Rate Limiting</span>
                </div>
                <Badge variant="success">Active</Badge>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="authentication" className="mt-6 space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="text-base">
                Authentication Settings
              </CardTitle>
              <CardDescription className="text-xs">
                Configure authentication policies
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="flex items-center justify-between">
                <div className="space-y-0.5">
                  <Label>Require MFA for all users</Label>
                  <p className="text-xs text-muted-foreground">
                    Force multi-factor authentication
                  </p>
                </div>
                <Switch checked={settings?.require_mfa ?? false} disabled />
              </div>
              <div className="flex items-center justify-between">
                <div className="space-y-0.5">
                  <Label>Allow password login</Label>
                  <p className="text-xs text-muted-foreground">
                    Enable email/password authentication
                  </p>
                </div>
                <Switch
                  checked={settings?.allow_password_login ?? true}
                  disabled
                />
              </div>
              <div className="flex items-center justify-between">
                <div className="space-y-0.5">
                  <Label>Allow SSO login</Label>
                  <p className="text-xs text-muted-foreground">
                    Enable single sign-on providers
                  </p>
                </div>
                <Switch checked={settings?.allow_sso_login ?? true} disabled />
              </div>
              <div className="space-y-2">
                <Label>Session timeout (minutes)</Label>
                <Input
                  type="number"
                  value={settings?.session_timeout_minutes ?? 60}
                  className="w-32"
                  disabled
                />
              </div>
              <div className="space-y-2">
                <Label>Password minimum length</Label>
                <Input
                  type="number"
                  value={settings?.password_min_length ?? 8}
                  className="w-32"
                  disabled
                />
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="ip-restrictions" className="mt-6 space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="text-base">IP Restrictions</CardTitle>
              <CardDescription className="text-xs">
                Control access by IP address
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between">
                <div className="space-y-0.5">
                  <Label>Enable IP whitelist</Label>
                  <p className="text-xs text-muted-foreground">
                    Only allow access from specified IPs
                  </p>
                </div>
                <Switch
                  checked={settings?.ip_whitelist_enabled ?? false}
                  disabled
                />
              </div>
              <div className="space-y-2">
                <Label>Whitelisted IPs</Label>
                {(settings?.whitelisted_ips?.length ?? 0) === 0 ? (
                  <div className="text-center py-8 text-muted-foreground">
                    <Globe className="h-12 w-12 mx-auto mb-4 opacity-50" />
                    <p>No IP restrictions configured</p>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {settings?.whitelisted_ips.map((ip, index) => (
                      <div
                        key={index}
                        className="flex items-center gap-2 p-2 border rounded"
                      >
                        <code className="text-sm">{ip}</code>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="audit-log" className="mt-6">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="text-base">Audit Log</CardTitle>
                  <CardDescription className="text-xs">
                    Recent security events and actions
                  </CardDescription>
                </div>
                <Select value={eventFilter} onValueChange={setEventFilter}>
                  <SelectTrigger className="w-40">
                    <SelectValue placeholder="Filter events" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Events</SelectItem>
                    <SelectItem value="login">Logins</SelectItem>
                    <SelectItem value="api">API Access</SelectItem>
                    <SelectItem value="admin">Admin Actions</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </CardHeader>
            <CardContent>
              {auditLoading && displayAuditLogs.length === 0 ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
                </div>
              ) : displayAuditLogs.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  <Clock className="h-12 w-12 mx-auto mb-4 opacity-50" />
                  <p>No audit events to display</p>
                </div>
              ) : (
                <>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead
                          className="cursor-pointer hover:bg-muted/50"
                          onClick={() => auditRequestSort("timestamp")}
                        >
                          Time {getAuditSortIcon("timestamp")}
                        </TableHead>
                        <TableHead
                          className="cursor-pointer hover:bg-muted/50"
                          onClick={() => auditRequestSort("user_email")}
                        >
                          User {getAuditSortIcon("user_email")}
                        </TableHead>
                        <TableHead
                          className="cursor-pointer hover:bg-muted/50"
                          onClick={() => auditRequestSort("action")}
                        >
                          Action {getAuditSortIcon("action")}
                        </TableHead>
                        <TableHead
                          className="cursor-pointer hover:bg-muted/50"
                          onClick={() => auditRequestSort("resource_type")}
                        >
                          Resource {getAuditSortIcon("resource_type")}
                        </TableHead>
                        <TableHead
                          className="cursor-pointer hover:bg-muted/50"
                          onClick={() => auditRequestSort("ip_address")}
                        >
                          IP Address {getAuditSortIcon("ip_address")}
                        </TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {displayAuditLogs.map((log) => (
                        <TableRow key={log.id}>
                          <TableCell className="text-sm text-muted-foreground">
                            {formatDate(log.timestamp)}
                          </TableCell>
                          <TableCell>{log.user_email}</TableCell>
                          <TableCell>
                            <Badge variant="outline">{log.action}</Badge>
                          </TableCell>
                          <TableCell className="text-sm">
                            {log.resource_type}:{log.resource_id}
                          </TableCell>
                          <TableCell className="text-sm text-muted-foreground">
                            {log.ip_address}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>

                  {/* Pagination */}
                  <div className="mt-4">
                    <Pagination
                      page={auditPagination.page}
                      totalPages={auditPagination.totalPages}
                      totalCount={auditPagination.totalCount}
                      pageSize={auditPagination.pageSize}
                      pageSizeOptions={[10, 25, 50, 100]}
                      startIndex={auditStartIndex}
                      endIndex={auditEndIndex}
                      canGoPrev={auditCanGoPrev}
                      canGoNext={auditCanGoNext}
                      onPageChange={auditGoToPage}
                      onPageSizeChange={auditSetPageSize}
                      onFirstPage={auditFirstPage}
                      onLastPage={auditLastPage}
                      onPrevPage={auditPrevPage}
                      onNextPage={auditNextPage}
                    />
                  </div>
                </>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
